import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { resetLearnedData } from '#banksync/ai-categorize';
import { Modal, ModalCloseButton, ModalHeader } from '#components/common/Modal';
import { Checkbox } from '#components/forms';
import type { Modal as ModalType } from '#modals/modalsSlice';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';

type AiRecommendationsModalProps = Extract<
  ModalType,
  { name: 'ai-recommendations' }
>['options'];

type Item = AiRecommendationsModalProps['items'][number];

type CategoryGroup = {
  categoryId: string;
  categoryName: string;
  items: Item[];
};

function formatAmount(amount: number): string {
  if (amount < 0) {
    return '-' + integerToCurrency(-amount);
  }
  return integerToCurrency(amount);
}

// group the recommendations by their suggested category, biggest groups first
function groupByCategory(items: Item[]): CategoryGroup[] {
  const groups = new Map<string, CategoryGroup>();
  for (const item of items) {
    let group = groups.get(item.categoryId);
    if (!group) {
      group = {
        categoryId: item.categoryId,
        categoryName: item.categoryName,
        items: [],
      };
      groups.set(item.categoryId, group);
    }
    group.items.push(item);
  }
  return [...groups.values()].sort((a, b) => b.items.length - a.items.length);
}

export function AiRecommendationsModal({ items }: AiRecommendationsModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // everything is selected by default | the common case is "accept all"
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(items.map(i => i.transactionId)),
  );

  // accordion: at most one group is open at a time | several open at once is
  // unreadable. starts with none open so a big list stays scannable.
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  const [filter, setFilter] = useState('');
  // two-click guard for the destructive "reset learned data" action
  const [confirmReset, setConfirmReset] = useState(false);
  const normalizedFilter = filter.trim().toLowerCase();
  const filtering = normalizedFilter.length > 0;

  const visibleItems = filtering
    ? items.filter(
        i =>
          i.payeeName.toLowerCase().includes(normalizedFilter) ||
          i.categoryName.toLowerCase().includes(normalizedFilter),
      )
    : items;

  const groups = useMemo(() => groupByCategory(visibleItems), [visibleItems]);

  const allSelected = selected.size === items.length;
  // while a filter is active every matching group is forced open
  const isExpanded = (categoryId: string) =>
    filtering || expandedGroup === categoryId;

  function toggleAll() {
    setSelected(
      allSelected ? new Set() : new Set(items.map(i => i.transactionId)),
    );
  }

  function toggleItem(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleGroup(group: CategoryGroup) {
    const ids = group.items.map(i => i.transactionId);
    const allOn = ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      for (const id of ids) {
        if (allOn) {
          next.delete(id);
        } else {
          next.add(id);
        }
      }
      return next;
    });
  }

  // accordion toggle: open this group and close whatever else was open
  function toggleGroupOpen(categoryId: string) {
    setExpandedGroup(prev => (prev === categoryId ? null : categoryId));
  }

  async function onAccept(close: () => void) {
    const selectedItems = items.filter(i => selected.has(i.transactionId));
    const updated = selectedItems.map(i => ({
      id: i.transactionId,
      category: i.categoryId,
    }));

    try {
      await send('transactions-batch-update', { updated });
      dispatch(
        addNotification({
          notification: {
            type: 'message',
            message: t('Categorized {{count}} transaction(s).', {
              count: selectedItems.length,
            }),
          },
        }),
      );
    } catch {
      dispatch(
        addNotification({
          notification: {
            type: 'error',
            message: t('Failed to apply category changes. Please try again.'),
          },
        }),
      );
      return;
    }

    close();
  }

  return (
    <Modal name="ai-recommendations">
      {({ state }) => (
        <View style={{ width: 'min(760px, 92vw)' }}>
          <ModalHeader
            title={t('AI category recommendations')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />

          {/* controls: select-all + filter */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              paddingBottom: 8,
              borderBottom: '1px solid ' + theme.tableBorder,
              marginBottom: 4,
            }}
          >
            <Checkbox
              id="ai-select-all"
              checked={allSelected}
              onChange={toggleAll}
            />
            <label
              htmlFor="ai-select-all"
              style={{
                cursor: 'pointer',
                fontSize: 13,
                color: theme.tableText,
                whiteSpace: 'nowrap',
              }}
            >
              {t('{{n}} of {{total}} selected', {
                n: selected.size,
                total: items.length,
              })}
            </label>
            <View style={{ flex: 1 }} />
            <Input
              placeholder={t('Filter payee or category...')}
              value={filter}
              onChangeValue={setFilter}
              style={{ width: 220 }}
            />
          </View>

          {/* scrollable grouped list */}
          <View
            style={{
              maxHeight: '60vh',
              overflowY: 'auto',
              marginBottom: 16,
            }}
          >
            {groups.length === 0 ? (
              <Text
                style={{
                  padding: 12,
                  fontSize: 13,
                  fontStyle: 'italic',
                  color: theme.pageTextSubdued,
                }}
              >
                <Trans>No matching transactions</Trans>
              </Text>
            ) : (
              groups.map(group => {
                const selectedInGroup = group.items.filter(i =>
                  selected.has(i.transactionId),
                ).length;
                const expanded = isExpanded(group.categoryId);
                return (
                  <View key={group.categoryId} style={{ flexShrink: 0 }}>
                    {/* group header | sticky so it stays visible while scrolling */}
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8,
                        height: 32,
                        flexShrink: 0,
                        paddingInline: 4,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: theme.tableBackground,
                        borderBottom: '1px solid ' + theme.tableBorder,
                      }}
                    >
                      <Checkbox
                        id={`ai-group-${group.categoryId}`}
                        checked={selectedInGroup === group.items.length}
                        onChange={() => toggleGroup(group)}
                      />
                      <View
                        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
                        role="button"
                        onClick={() => toggleGroupOpen(group.categoryId)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 6,
                          cursor: 'pointer',
                        }}
                      >
                        <Text
                          style={{
                            width: 12,
                            flexShrink: 0,
                            fontSize: 10,
                            color: theme.tableTextLight,
                          }}
                        >
                          {expanded ? '▾' : '▸'}
                        </Text>
                        <Text
                          title={group.categoryName}
                          style={{
                            fontWeight: 600,
                            fontSize: 13,
                            color: theme.tableText,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {group.categoryName}
                        </Text>
                        <Text
                          style={{ fontSize: 12, color: theme.pageTextSubdued }}
                        >
                          {selectedInGroup === group.items.length
                            ? `(${group.items.length})`
                            : `(${selectedInGroup}/${group.items.length})`}
                        </Text>
                      </View>
                    </View>

                    {/* group rows | bracketed + indented so the open group reads as one unit */}
                    {expanded && (
                      <View
                        style={{
                          flexShrink: 0,
                          borderLeft: '2px solid ' + theme.tableBorder,
                          marginLeft: 8,
                          marginBottom: 8,
                        }}
                      >
                        {group.items.map(item => (
                          <View
                            key={item.transactionId}
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 10,
                              height: 30,
                              flexShrink: 0,
                              paddingLeft: 16,
                              paddingRight: 4,
                              borderBottom: '1px solid ' + theme.tableBorder,
                            }}
                          >
                            <Checkbox
                              id={`ai-item-${item.transactionId}`}
                              checked={selected.has(item.transactionId)}
                              onChange={() => toggleItem(item.transactionId)}
                            />
                            <Text
                              style={{
                                width: 80,
                                flexShrink: 0,
                                fontSize: 12,
                                color: theme.tableTextLight,
                              }}
                            >
                              {item.date}
                            </Text>
                            <Text
                              title={item.payeeName}
                              style={{
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: 13,
                                color: theme.tableText,
                              }}
                            >
                              {item.payeeName}
                            </Text>
                            <Text
                              style={{
                                ...styles.tnum,
                                width: 84,
                                flexShrink: 0,
                                textAlign: 'right',
                                fontSize: 13,
                                color:
                                  item.amount < 0
                                    ? theme.errorText
                                    : theme.noticeTextLight,
                              }}
                            >
                              {formatAmount(item.amount)}
                            </Text>
                            <Text
                              style={{
                                flexShrink: 0,
                                fontSize: 11,
                                color: theme.tableTextLight,
                                backgroundColor: theme.tableBackground,
                                border: '1px solid ' + theme.tableBorder,
                                borderRadius: 3,
                                padding: '1px 4px',
                              }}
                            >
                              {item.source === 'llm' ? t('AI') : t('learned')}
                            </Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>
                );
              })
            )}
          </View>

          {/* footer */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Button
              variant="bare"
              style={{ color: theme.errorText }}
              onPress={async () => {
                if (!confirmReset) {
                  setConfirmReset(true);
                  return;
                }
                try {
                  const removed = await resetLearnedData();
                  dispatch(
                    addNotification({
                      notification: {
                        type: 'message',
                        message: t(
                          'Cleared {{count}} learned example(s). Run again for fresh suggestions.',
                          { count: removed },
                        ),
                      },
                    }),
                  );
                  state.close();
                } catch {
                  setConfirmReset(false);
                  dispatch(
                    addNotification({
                      notification: {
                        type: 'error',
                        message: t('Failed to reset learned data.'),
                      },
                    }),
                  );
                }
              }}
            >
              {confirmReset
                ? t('Click again to confirm reset')
                : t('Reset learned data')}
            </Button>
            <View style={{ flex: 1 }} />
            <Button onPress={() => state.close()}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              variant="primary"
              isDisabled={selected.size === 0}
              onPress={() => onAccept(() => state.close())}
            >
              <Trans>Accept {{ count: selected.size }} selected</Trans>
            </Button>
          </View>
        </View>
      )}
    </Modal>
  );
}
